import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';

const ATFF_LOGO = "https://customer-assets.emergentagent.com/job_africa-trade-hub-5/artifacts/0f6s53y1_Untitled%20design%20%285%29.png";

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useTranslation();

  const footerLinks = {
    platform: [
      { label: t('nav.marketplace'), href: '/marketplace' },
      { label: t('nav.tradeCompliance'), href: '/compliance-engine' },
      { label: t('nav.logistics'), href: '/logistics' },
      { label: t('nav.tradeFinance'), href: '/trade-finance' },
      { label: t('nav.documents'), href: '/documents' },
    ],
    resources: [
      { label: t('footer.afcftaGuide'), href: 'https://customer-assets.emergentagent.com/job_africa-trade-hub-5/artifacts/k6xemyqo_AfCFTA%20Guide.pdf', external: true },
      { label: t('footer.tradeDocumentation'), href: 'https://customer-assets.emergentagent.com/job_africa-trade-hub-5/artifacts/luih6au0_Trade%20Documentation.pdf', external: true },
      { label: t('footer.rulesOfOrigin'), href: 'https://customer-assets.emergentagent.com/job_africa-trade-hub-5/artifacts/o84h8vij_Rues%20of%20Origin.pdf', external: true },
      { label: t('footer.helpCenter'), href: 'https://customer-assets.emergentagent.com/job_africa-trade-hub-5/artifacts/w0349j7w_Help%20Center%20PDF.pdf', external: true },
      { label: t('footer.faqs'), href: 'https://customer-assets.emergentagent.com/job_africa-trade-hub-5/artifacts/ohxt6uab_FAQs.html', external: true },
    ],
    company: [
      { label: t('footer.aboutAtff'), href: 'https://customer-assets.emergentagent.com/job_africa-trade-hub-5/artifacts/ziz4lgqm_About%20ATFF.pdf', external: true },
      { label: t('footer.partners'), href: 'https://customer-assets.emergentagent.com/job_africa-trade-hub-5/artifacts/g20nntsd_Partners.pdf', external: true },
      { label: t('footer.nationalNodes'), href: 'https://customer-assets.emergentagent.com/job_africa-trade-hub-5/artifacts/xrtmcn5q_National%20Nodes.pdf', external: true },
      { label: t('footer.press'), href: 'https://customer-assets.emergentagent.com/job_africa-trade-hub-5/artifacts/t1cxhouv_Press.html', external: true },
      { label: t('footer.contact'), href: 'https://customer-assets.emergentagent.com/job_africa-trade-hub-5/artifacts/vf0dub7a_Contact.pdf', external: true },
    ],
  };

  return (
    <footer className="bg-charcoal text-white" data-testid="footer">
      <div className="container-main py-16">
        {/* Top Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-4">
              <img 
                src={ATFF_LOGO} 
                alt="ATFF - Africa Trading Free Flow Portal" 
                className="h-28 md:h-32 w-auto object-contain"
              />
            </Link>
            <p className="text-gray-400 mb-6 max-w-sm leading-relaxed">
              {t('footer.description')}
            </p>
            <div className="flex items-center gap-4">
              <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-continental transition-colors" aria-label="Facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-continental transition-colors" aria-label="Twitter">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-continental transition-colors" aria-label="LinkedIn">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-continental transition-colors" aria-label="Instagram">
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Platform Links */}
          <div>
            <h4 className="font-poppins font-semibold text-lg mb-4">{t('footer.platform')}</h4>
            <ul className="space-y-3">
              {footerLinks.platform.map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.href} 
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Links */}
          <div>
            <h4 className="font-poppins font-semibold text-lg mb-4">{t('footer.resources')}</h4>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.label}>
                  {link.external ? (
                    <a 
                      href={link.href}
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link 
                      to={link.href} 
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-poppins font-semibold text-lg mb-4">{t('footer.company')}</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  {link.external ? (
                    <a 
                      href={link.href}
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link 
                      to={link.href} 
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contact Info */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-wrap gap-6 text-gray-400 text-sm">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <span>info@atfftrade.africa</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              <span>+234 800 ATFF TRADE</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span>Abuja, Nigeria</span>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-8 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">
            © {currentYear} ATFFtrade.africa. {t('footer.copyright')}
          </p>
          <div className="flex items-center gap-6 text-sm text-gray-400">
            <a href="https://customer-assets.emergentagent.com/job_africa-trade-hub-5/artifacts/tz3fbh0h_Privacy%20Policy.pdf" rel="noopener noreferrer" className="hover:text-white transition-colors">{t('footer.privacyPolicy')}</a>
            <a href="https://customer-assets.emergentagent.com/job_africa-trade-hub-5/artifacts/wpoe552b_Terms%20of%20Service.pdf" rel="noopener noreferrer" className="hover:text-white transition-colors">{t('footer.termsOfService')}</a>
            <a href="https://customer-assets.emergentagent.com/job_africa-trade-hub-5/artifacts/ckur5ieq_Cookie%20Policy.pdf" rel="noopener noreferrer" className="hover:text-white transition-colors">{t('footer.cookiePolicy')}</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
